import React from 'react';
import { Container, Typography, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';

const buildPrivacySchema = (lng) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Politica de Privacidad - FinMaple',
  description: 'Conoce como FinMaple recopila, usa y protege tu informacion personal. Respetamos tu privacidad.',
  url: 'https://www.finmaple.com/privacidad',
  publisher: {
    '@type': 'Organization',
    name: 'FinMaple',
    url: 'https://www.finmaple.com',
  },
  inLanguage: lng,
  availableLanguage: ['es', 'en', 'fr'],
});

const PoliticaPrivacidadPage = () => {
  const { i18n } = useTranslation();
  return (
    <Box sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        seoKey="privacy"
        title="Politica de Privacidad"
        description="Conoce como FinMaple recopila, usa y protege tu informacion personal. Usamos analiticas que respetan tu privacidad."
        canonical="/privacidad"
        schema={buildPrivacySchema(i18n.language)}
        routeKey="privacy"
        noIndex={i18n.language !== 'es'}
      />

      <PageHeader
        variant="functional"
        title="Politica de Privacidad"
        subtitle="Tu privacidad es importante para nosotros."
        breadcrumbs={[{ label: 'Politica de Privacidad' }]}
      />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          }}
        >
          {/* Intro */}
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            En FinMaple (finmaple.com) nos comprometemos a proteger tu privacidad. Esta politica explica que informacion recopilamos, como la usamos y cuales son tus derechos. FinMaple es un sitio de educacion financiera y comparacion de productos; no somos una institucion financiera ni procesamos transacciones.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 1: Data collected */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            1. Informacion que recopilamos
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple recopila una cantidad minima de informacion, limitada a lo necesario para operar y mejorar el sitio:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Analiticas de uso:</strong> Utilizamos Plausible Analytics, una herramienta de analitica web que respeta la privacidad y no usa cookies. Adicionalmente, utilizamos Google Analytics (GA4) para obtener metricas complementarias. Google Analytics puede utilizar cookies y recopilar datos como tu direccion IP (anonimizada), paginas visitadas y tipo de dispositivo. Puedes consultar la politica de privacidad de Google en policies.google.com/privacy.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Correo electronico (newsletter):</strong> Si te suscribes a nuestro boletin, recopilamos tu direccion de correo electronico. Este dato se usa unicamente para enviarte contenido de FinMaple.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Formulario de contacto:</strong> Si nos escribes a traves del formulario de contacto, recopilamos tu nombre, correo electronico y el mensaje que nos envias para poder responderte.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Maya (asistente virtual):</strong> Si usas nuestro chatbot Maya, tus mensajes se envian a un servicio externo de inteligencia artificial (Anthropic) para generar respuestas. El historial de conversacion se almacena localmente en tu navegador (localStorage) durante 7 dias y no se guarda en nuestros servidores. No compartas informacion personal sensible en el chat.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            No recopilamos informacion financiera, numeros de identificacion, ni datos sensibles de ningun tipo.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 2: How data is used */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            2. Como usamos tu informacion
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            La informacion que recopilamos se usa exclusivamente para:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Mejorar el sitio:</strong> Las metricas agregadas de Plausible nos ayudan a entender que contenido es mas util para nuestra audiencia y como mejorar la experiencia de navegacion.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Comunicacion:</strong> Si te suscribiste al newsletter o nos contactaste, usamos tu correo para responderte o enviarte actualizaciones de contenido. No vendemos ni compartimos tu correo con terceros.
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 3: Third-party services */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            3. Servicios de terceros
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple utiliza los siguientes servicios de terceros:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Plausible Analytics:</strong> Servicio de analitica web enfocado en la privacidad. No utiliza cookies, cumple con GDPR y CCPA, y no recopila datos personales. Mas informacion en <a href="https://plausible.io/data-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#1E3A5F' }}>plausible.io/data-policy</a>.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Enlaces de afiliados:</strong> Nuestro sitio contiene enlaces a productos financieros de socios como Borrowell, Neo Financial, Tangerine y Scotiabank. Cuando haces clic en estos enlaces, eres redirigido al sitio web del proveedor, que tiene su propia politica de privacidad. FinMaple no controla ni es responsable de las practicas de privacidad de estos sitios externos.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            No vendemos, alquilamos ni compartimos tus datos personales con terceros con fines publicitarios o de marketing.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 4: Cookies */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            4. Cookies
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple utiliza un enfoque minimalista con respecto a las cookies:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Plausible Analytics no usa cookies.</strong> Plausible funciona sin cookies ni rastreo individual. Sin embargo, tambien utilizamos Google Analytics (GA4), que puede usar cookies para metricas complementarias. Puedes desactivar las cookies de Google Analytics en la configuracion de tu navegador.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Cookies esenciales:</strong> Es posible que se utilicen cookies estrictamente necesarias para el funcionamiento tecnico del sitio (por ejemplo, preferencias de sesion). Estas cookies no rastrean tu actividad ni recopilan datos personales.
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 5: User rights */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            5. Tus derechos
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Tienes derecho a:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Acceder</strong> a la informacion personal que tenemos sobre ti.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Solicitar la eliminacion</strong> de tu informacion personal de nuestros registros.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Darte de baja</strong> del newsletter en cualquier momento usando el enlace de cancelacion incluido en cada correo, o escribiendonos directamente.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Para ejercer cualquiera de estos derechos, escribenos a <strong>hola@finmaple.com</strong>. Responderemos a tu solicitud en un plazo maximo de 30 dias.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 6: Data retention */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            6. Retencion de datos
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Conservamos tu informacion personal solo durante el tiempo necesario para cumplir con los fines descritos en esta politica:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Correos del newsletter:</strong> Hasta que te des de baja o solicites la eliminacion.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Datos del formulario de contacto:</strong> Hasta 12 meses despues de resolver tu consulta.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Datos de analitica:</strong> Plausible retiene datos agregados (no personales) de forma indefinida. No se almacenan datos individuales.
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 7: Changes */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            7. Cambios a esta politica
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Podemos actualizar esta politica de privacidad ocasionalmente. Cualquier cambio sera publicado en esta misma pagina con la fecha de actualizacion correspondiente. Te recomendamos revisarla periodicamente.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 8: Contact */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            8. Contacto
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Si tienes preguntas sobre esta politica de privacidad o sobre como manejamos tu informacion, puedes contactarnos en:
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            <strong>Correo electronico:</strong> hola@finmaple.com
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Last updated */}
          <Typography variant="body2" sx={{ color: '#5F6B7A', fontStyle: 'italic' }}>
            Ultima actualizacion: Febrero 2026
          </Typography>
        </Box>

        {/* Disclaimer */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, fontStyle: 'italic' }}>
            Esta politica de privacidad aplica exclusivamente al sitio web finmaple.com. Los sitios de terceros enlazados desde FinMaple tienen sus propias politicas de privacidad, sobre las cuales no tenemos control ni responsabilidad.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PoliticaPrivacidadPage;
